import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../services/adminApi';
import { Plus, Edit, Trash2, Calendar, Mail, Phone } from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';
import RichBlogEditor from '../../components/RichBlogEditor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const AllContentManagement = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Check URL params for initial tab
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get('tab') || 'blog';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Blog state
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);
  
  // Regions state
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  
  // Home Content state
  const [homeContent, setHomeContent] = useState({
    hero: {},
    intro: {},
    about: {},
    stats: {},
    headerFooter: {}
  });
  const [loadingHomeContent, setLoadingHomeContent] = useState(false);
  const [editingSection, setEditingSection] = useState(null);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Rich Blog Editor state
  const [showRichEditor, setShowRichEditor] = useState(false);
  const [richEditorData, setRichEditorData] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    if (!loading && isAuthenticated) {
      loadDataForTab(activeTab);
    }
  }, [loading, isAuthenticated, navigate, activeTab]);

  const loadDataForTab = (tab) => {
    switch(tab) {
      case 'blog':
        loadPosts();
        break;
      case 'testimonials':
        loadTestimonials();
        break;
      case 'regions':
        loadRegions();
        break;
      case 'contacts':
        loadContacts();
        break;
      case 'home':
        loadHomeContent();
        break;
      default:
        break;
    }
  };

  const loadPosts = async () => {
    setLoadingPosts(true);
    try {
      const token = localStorage.getItem('admin_token');
      const data = await adminApi.getAllBlogPosts(token);
      setPosts(data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar posts',
        variant: 'destructive'
      });
    } finally {
      setLoadingPosts(false);
    }
  };

  const loadTestimonials = async () => {
    setLoadingTestimonials(true);
    try {
      const token = localStorage.getItem('admin_token');
      const data = await adminApi.getAllTestimonials(token);
      setTestimonials(data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar depoimentos',
        variant: 'destructive'
      });
    } finally {
      setLoadingTestimonials(false);
    }
  };

  const loadRegions = async () => {
    setLoadingRegions(true);
    try {
      const token = localStorage.getItem('admin_token');
      const data = await adminApi.getAllRegions(token);
      setRegions(data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar regiões',
        variant: 'destructive'
      });
    } finally {
      setLoadingRegions(false);
    }
  };

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      const token = localStorage.getItem('admin_token');
      const data = await adminApi.getAllContacts(token);
      setContacts(data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar mensagens',
        variant: 'destructive'
      });
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadHomeContent = async () => {
    setLoadingHomeContent(true);
    try {
      const token = localStorage.getItem('admin_token');
      const [hero, intro, about, stats, headerFooter, services] = await Promise.all([
        adminApi.getHeroSection(token),
        adminApi.getIntroSection(token),
        adminApi.getAboutSection(token),
        adminApi.getStatsSection(token),
        adminApi.getHeaderFooter(token),
        adminApi.getServicesSection(token)
      ]);
      setHomeContent({ hero, intro, about, stats, headerFooter, services });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar conteúdo da home',
        variant: 'destructive'
      });
    } finally {
      setLoadingHomeContent(false);
    }
  };

  const handleEditHomeSection = (section, data) => {
    setEditingSection(section);
    setFormData(data);
    setIsDialogOpen(true);
  };

  const handleUpdateHomeSection = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      switch(editingSection) {
        case 'hero':
          await adminApi.updateHeroSection(token, formData);
          break;
        case 'intro':
          await adminApi.updateIntroSection(token, formData);
          break;
        case 'about':
          await adminApi.updateAboutSection(token, formData);
          break;
        case 'stats':
          await adminApi.updateStatsSection(token, formData);
          break;
        case 'headerFooter':
          await adminApi.updateHeaderFooter(token, formData);
          break;
        case 'services':
          await adminApi.updateServicesSection(token, formData);
          break;
        default:
          break;
      }
      
      toast({
        title: 'Sucesso',
        description: 'Conteúdo atualizado com sucesso'
      });
      
      setIsDialogOpen(false);
      loadHomeContent();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar conteúdo',
        variant: 'destructive'
      });
    }
  };


  const handleCreate = (type) => {
    setEditingItem(null);
    
    switch(type) {
      case 'blog':
        // Abrir editor rico para blog
        setRichEditorData(null);
        setShowRichEditor(true);
        break;
      case 'testimonial':
        setFormData({
          id: Date.now().toString(),
          text: '',
          author: '',
          role: '',
          active: true
        });
        setIsDialogOpen(true);
        break;
      case 'region':
        setFormData({
          id: Date.now().toString(),
          name: '',
          description: '',
          image: '',
          priceRange: '',
          order: regions.length,
          active: true
        });
        setIsDialogOpen(true);
        break;
      default:
        break;
    }
  };

  const handleEdit = (item, type) => {
    if (type === 'blog') {
      // Abrir editor rico para blog
      setRichEditorData(item);
      setShowRichEditor(true);
    } else {
      setEditingItem({ ...item, type });
      setFormData(item);
      setIsDialogOpen(true);
    }
  };

  const handleRichBlogSave = async (postData) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      if (richEditorData) {
        // Update existing post
        await adminApi.updateBlogPost(token, postData.id, postData);
        toast({
          title: 'Sucesso',
          description: 'Post atualizado com sucesso'
        });
      } else {
        // Create new post
        await adminApi.createBlogPost(token, postData);
        toast({
          title: 'Sucesso',
          description: 'Post criado com sucesso'
        });
      }
      
      setShowRichEditor(false);
      setRichEditorData(null);
      loadPosts();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao salvar post',
        variant: 'destructive'
      });
    }
  };

  const handleRichBlogCancel = () => {
    setShowRichEditor(false);
    setRichEditorData(null);
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Tem certeza que deseja deletar este item?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      
      switch(type) {
        case 'blog':
          await adminApi.deleteBlogPost(token, id);
          await loadPosts();
          break;
        case 'testimonial':
          await adminApi.deleteTestimonial(token, id);
          await loadTestimonials();
          break;
        case 'region':
          await adminApi.deleteRegion(token, id);
          await loadRegions();
          break;
        default:
          break;
      }
      
      toast({
        title: 'Sucesso',
        description: 'Item deletado com sucesso'
      });
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao deletar item',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const type = editingItem?.type || activeTab;
      
      if (editingItem) {
        // Update
        switch(type) {
          case 'blog':
            await adminApi.updateBlogPost(token, formData.id, formData);
            loadPosts();
            break;
          case 'testimonial':
            await adminApi.updateTestimonial(token, formData.id, formData);
            loadTestimonials();
            break;
          case 'region':
            await adminApi.updateRegion(token, formData.id, formData);
            loadRegions();
            break;
          default:
            break;
        }
        toast({
          title: 'Sucesso',
          description: 'Item atualizado com sucesso'
        });
      } else {
        // Create
        switch(type) {
          case 'blog':
            await adminApi.createBlogPost(token, formData);
            loadPosts();
            break;
          case 'testimonials':
            await adminApi.createTestimonial(token, formData);
            loadTestimonials();
            break;
          case 'regions':
            await adminApi.createRegion(token, formData);
            loadRegions();
            break;
          default:
            break;
        }
        toast({
          title: 'Sucesso',
          description: 'Item criado com sucesso'
        });
      }

      setIsDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao salvar item',
        variant: 'destructive'
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : (name === 'order' ? parseInt(value) || 0 : value)
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não disponível';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading while authentication is being verified
  if (loading) {
    return (
      <AdminLayout title="Gerenciar Conteúdo">
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-lg text-slate-600">Carregando...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gerenciar Conteúdo">
      <div className="mb-4 sm:mb-6">
        {/* Tabs - Responsive */}
        <div className="flex gap-1 sm:gap-2 border-b border-slate-200 overflow-x-auto pb-0">
          <Button
            variant={activeTab === 'blog' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('blog')}
            className="rounded-b-none text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4"
            size="sm"
          >
            Blog
          </Button>
          <Button
            variant={activeTab === 'testimonials' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('testimonials')}
            className="rounded-b-none text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4"
            size="sm"
          >
            Depoimentos
          </Button>
          <Button
            variant={activeTab === 'regions' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('regions')}
            className="rounded-b-none text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4"
            size="sm"
          >
            Regiões
          </Button>
          <Button
            variant={activeTab === 'home' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('home')}
            className="rounded-b-none text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4"
            size="sm"
          >
            Home
          </Button>
        </div>
      </div>

      <div>
        {/* Blog Posts Tab */}
        {activeTab === 'blog' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
              <h2 className="text-xl sm:text-2xl font-semibold">Blog Posts</h2>
              <Button onClick={() => handleCreate('blog')} className="bg-[#2E5E99] hover:bg-[#0D2440] text-white w-full sm:w-auto" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Novo Post
              </Button>
            </div>
            
            {loadingPosts ? (
              <p className="text-sm">Carregando...</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {posts.map((post) => (
                  <Card key={post.id}>
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                        <div className="flex-1 w-full">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:space-x-3 mb-2">
                            <h3 className="text-base sm:text-lg font-semibold">{post.title}</h3>
                            <div className="flex gap-2">
                              <span className={`text-xs px-2 py-1 rounded self-start ${post.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                {post.published ? 'Publicado' : 'Rascunho'}
                              </span>
                              {post.unlisted && (
                                <span className="text-xs px-2 py-1 rounded self-start bg-amber-100 text-amber-700">
                                  Não Listado
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="text-xs sm:text-sm text-slate-600 mb-2 line-clamp-2">{post.excerpt}</p>
                          <span className="text-xs text-slate-500">{post.category}</span>
                        </div>
                        <div className="flex space-x-2 w-full sm:w-auto">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(post, 'blog')} className="flex-1 sm:flex-none">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(post.id, 'blog')} className="flex-1 sm:flex-none">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {posts.length === 0 && (
                  <p className="text-center text-slate-500 py-12">Nenhum post encontrado</p>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">Depoimentos</h2>
              <Button onClick={() => handleCreate('testimonial')} className="bg-[#2E5E99] hover:bg-[#0D2440] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Novo Depoimento
              </Button>
            </div>
            
            {loadingTestimonials ? (
              <p>Carregando...</p>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {testimonials.map((testimonial) => (
                  <Card key={testimonial.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold">{testimonial.author}</h3>
                            <span className={`text-xs px-2 py-1 rounded ${testimonial.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                              {testimonial.active ? 'Ativo' : 'Inativo'}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">{testimonial.role}</p>
                          <p className="text-sm text-slate-700 italic">"{testimonial.text}"</p>
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(testimonial, 'testimonial')}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(testimonial.id, 'testimonial')}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {testimonials.length === 0 && (
                  <p className="text-center text-slate-500 py-12">Nenhum depoimento encontrado</p>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Regions Tab */}
        {activeTab === 'regions' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">Regiões</h2>
              <Button onClick={() => handleCreate('region')} className="bg-[#2E5E99] hover:bg-[#0D2440] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Nova Região
              </Button>
            </div>
            
            {loadingRegions ? (
              <p>Carregando...</p>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {regions.map((region) => (
                  <Card key={region.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4 flex-1">
                          {region.image && (
                            <img 
                              src={region.image} 
                              alt={region.name}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-semibold">{region.name}</h3>
                              <span className={`text-xs px-2 py-1 rounded ${region.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                {region.active ? 'Ativo' : 'Inativo'}
                              </span>
                              <span className="text-xs px-2 py-1 rounded bg-brand-light text-blue-700">
                                Ordem: {region.order}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{region.description}</p>
                            <p className="text-sm text-slate-700 font-medium">{region.priceRange}</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(region, 'region')}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(region.id, 'region')}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {regions.length === 0 && (
                  <p className="text-center text-slate-500 py-12">Nenhuma região encontrada</p>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">Mensagens de Contacto</h2>
              <p className="text-sm text-slate-600">
                Total: <span className="font-semibold">{contacts.length}</span>
              </p>
            </div>
            
            {loadingContacts ? (
              <p>Carregando...</p>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {contacts.map((message) => (
                  <Card key={message.id}>
                    <CardContent className="p-6">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between border-b pb-3">
                          <div>
                            <h3 className="text-lg font-semibold text-slate-800">{message.name}</h3>
                            <div className="flex items-center gap-4 mt-1">
                              <div className="flex items-center gap-1 text-sm text-slate-600">
                                <Mail className="w-4 h-4" />
                                <a href={`mailto:${message.email}`} className="hover:text-brand-dark">
                                  {message.email}
                                </a>
                              </div>
                              {message.phone && (
                                <div className="flex items-center gap-1 text-sm text-slate-600">
                                  <Phone className="w-4 h-4" />
                                  <a href={`tel:${message.phone}`} className="hover:text-brand-dark">
                                    {message.phone}
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {formatDate(message.createdAt)}
                          </div>
                        </div>
                        
                        {/* Localização pretendida */}
                        {message.location && (
                          <div>
                            <label className="text-xs font-medium text-slate-500 uppercase">Localização pretendida:</label>
                            <p className="text-sm text-slate-700 mt-1">{message.location}</p>
                          </div>
                        )}
                        
                        {/* Faixa de preço */}
                        {message.priceRange && (
                          <div>
                            <label className="text-xs font-medium text-slate-500 uppercase">Faixa de preço:</label>
                            <p className="text-sm text-slate-700 mt-1">{message.priceRange}</p>
                          </div>
                        )}
                        
                        {/* Observações/Mensagem */}
                        {(message.observations || message.message) && (
                          <div>
                            <label className="text-xs font-medium text-slate-500 uppercase">Observações:</label>
                            <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{message.observations || message.message}</p>
                          </div>
                        )}
                        
                        {message.propertyInterest && (
                          <div>
                            <label className="text-xs font-medium text-slate-500 uppercase">Interesse em propriedade:</label>
                            <p className="text-sm text-slate-700 mt-1">{message.propertyInterest}</p>
                          </div>
                        )}
                        
                        {/* Botão para responder por email */}
                        <div className="flex items-center justify-between border-t pt-3">
                          <span className={`px-2 py-1 rounded text-xs ${message.privacyAccepted ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {message.privacyAccepted ? 'Privacidade aceita' : 'Privacidade não aceita'}
                          </span>
                          <a 
                            href={`mailto:${message.email}?subject=Re: Contacto - Your Broker Portugal&body=Olá ${message.name},%0D%0A%0D%0AObrigado pelo seu contacto.%0D%0A%0D%0A----%0D%0ASua mensagem:%0D%0A${encodeURIComponent(message.observations || message.message || '')}%0D%0A${message.location ? 'Localização: ' + encodeURIComponent(message.location) + '%0D%0A' : ''}${message.priceRange ? 'Faixa de preço: ' + encodeURIComponent(message.priceRange) : ''}`}
                            className="px-4 py-2 bg-[#2E5E99] hover:bg-[#0D2440] text-white rounded text-sm font-medium transition-colors"
                          >
                            Responder por Email
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {contacts.length === 0 && (
                  <p className="text-center text-slate-500 py-12">Nenhuma mensagem encontrada</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Home Content Tab */}
        {activeTab === 'home' && (
          <div>
            <h2 className="text-2xl font-semibold mb-6">Gerenciar Conteúdo da Home</h2>
            
            {loadingHomeContent ? (
              <p>Carregando...</p>
            ) : (
              <div className="space-y-6">
                {/* Hero Section */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Seção Hero</h3>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditHomeSection('hero', homeContent.hero)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><strong>Título:</strong> {homeContent.hero?.title}</p>
                      <p><strong>Subtítulo:</strong> {homeContent.hero?.subtitle}</p>
                      {homeContent.hero?.image && (
                        <div>
                          <strong>Imagem:</strong>
                          <img src={homeContent.hero.image} alt="Hero" className="mt-2 w-full h-32 object-cover rounded" />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Intro Section */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Seção Intro</h3>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditHomeSection('intro', homeContent.intro)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><strong>Título:</strong> {homeContent.intro?.title}</p>
                      <p><strong>Descrição:</strong> {homeContent.intro?.description}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* About Section */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Seção Sobre</h3>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditHomeSection('about', homeContent.about)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><strong>Título:</strong> {homeContent.about?.title}</p>
                      <p><strong>Descrição:</strong> {homeContent.about?.description}</p>
                      <p><strong>Destaque:</strong> {homeContent.about?.highlight}</p>
                      {homeContent.about?.image && (
                        <div>
                          <strong>Imagem:</strong>
                          <img src={homeContent.about.image} alt="About" className="mt-2 w-full h-32 object-cover rounded" />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Section */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Seção Estatísticas</h3>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditHomeSection('stats', homeContent.stats)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-2xl">{homeContent.stats?.stat1_value}</p>
                        <p className="text-slate-600">{homeContent.stats?.stat1_label}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-2xl">{homeContent.stats?.stat2_value}</p>
                        <p className="text-slate-600">{homeContent.stats?.stat2_label}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-2xl">{homeContent.stats?.stat3_value}</p>
                        <p className="text-slate-600">{homeContent.stats?.stat3_label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Header/Footer Section */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Header / Footer</h3>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditHomeSection('headerFooter', homeContent.headerFooter)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><strong>Nome da Empresa:</strong> {homeContent.headerFooter?.companyName}</p>
                      <p><strong>Email:</strong> {homeContent.headerFooter?.email}</p>
                      <p><strong>Telefone:</strong> {homeContent.headerFooter?.phone}</p>
                      <p><strong>Endereço:</strong> {homeContent.headerFooter?.address}</p>
                    </div>
                  </CardContent>
                </Card>


                {/* Services Section */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Seção Serviços</h3>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditHomeSection('services', homeContent.services)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><strong>Título:</strong> {homeContent.services?.title}</p>
                      <p><strong>Subtítulo:</strong> {homeContent.services?.subtitle}</p>
                      <p><strong>Serviços:</strong> {homeContent.services?.services?.length || 0} itens</p>
                    </div>
                  </CardContent>
                </Card>

              </div>
            )}
          </div>
        )}

      </div>

      {/* Dialog for Create/Edit */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSection ? (
                `Editar ${
                  editingSection === 'hero' ? 'Hero' : 
                  editingSection === 'intro' ? 'Intro' : 
                  editingSection === 'about' ? 'Sobre' : 
                  editingSection === 'stats' ? 'Estatísticas' : 
                  editingSection === 'services' ? 'Serviços' :
                  'Header/Footer'
                }`
              ) : (
                `${editingItem ? 'Editar' : 'Criar'} ${activeTab === 'blog' ? 'Post' : activeTab === 'testimonials' ? 'Depoimento' : 'Região'}`
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={editingSection ? handleUpdateHomeSection : handleSubmit} className="space-y-4">
            {/* Blog Form */}
            {!editingSection && (activeTab === 'blog' || editingItem?.type === 'blog') && (
              <>
                {/* Portuguese Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇵🇹</span> Português
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Título (PT)</label>
                      <Input name="title" value={formData.title || ''} onChange={handleChange} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Resumo (PT)</label>
                      <Textarea name="excerpt" value={formData.excerpt || ''} onChange={handleChange} rows={2} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Conteúdo (PT)</label>
                      <Textarea name="content" value={formData.content || ''} onChange={handleChange} rows={6} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Categoria (PT)</label>
                      <Input name="category" value={formData.category || ''} onChange={handleChange} required />
                    </div>
                  </div>
                </div>

                {/* English Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇬🇧</span> English
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Title (EN)</label>
                      <Input name="title_en" value={formData.title_en || ''} onChange={handleChange} placeholder="English translation of title" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Excerpt (EN)</label>
                      <Textarea name="excerpt_en" value={formData.excerpt_en || ''} onChange={handleChange} rows={2} placeholder="English translation of excerpt" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Content (EN)</label>
                      <Textarea name="content_en" value={formData.content_en || ''} onChange={handleChange} rows={6} placeholder="English translation of content" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Category (EN)</label>
                      <Input name="category_en" value={formData.category_en || ''} onChange={handleChange} placeholder="English translation of category" />
                    </div>
                  </div>
                </div>

                {/* SEO Slug Field */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🔗</span> SEO Slug
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-sm font-medium">Slug (URL amigável)</label>
                      <Input 
                        name="slug" 
                        value={formData.slug || ''} 
                        onChange={handleChange} 
                        placeholder="exemplo: guia-investir-portugal-2025"
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Preview: yourbrokerportugal.com/blog/{formData.slug || 'slug-automatico'}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        💡 Deixe vazio para gerar automaticamente a partir do título
                      </p>
                    </div>
                  </div>
                </div>

                {/* Common Fields */}
                <ImageUpload
                  label="Imagem"
                  value={formData.image || ''}
                  onChange={(url) => setFormData(prev => ({ ...prev, image: url }))}
                  required
                />
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="published"
                    name="published"
                    checked={formData.published || false}
                    onChange={handleChange}
                  />
                  <label htmlFor="published" className="text-sm font-medium">Publicado</label>
                </div>
              </>
            )}

            {/* Testimonial Form */}
            {!editingSection && (activeTab === 'testimonials' || editingItem?.type === 'testimonial') && (
              <>
                {/* Portuguese Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇵🇹</span> Português
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Depoimento (PT)</label>
                      <Textarea name="text" value={formData.text || ''} onChange={handleChange} rows={6} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Cargo/Função (PT)</label>
                      <Input name="role" value={formData.role || ''} onChange={handleChange} required />
                    </div>
                  </div>
                </div>

                {/* English Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇬🇧</span> English
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Testimonial (EN)</label>
                      <Textarea name="text_en" value={formData.text_en || ''} onChange={handleChange} rows={6} placeholder="English translation" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Role (EN)</label>
                      <Input name="role_en" value={formData.role_en || ''} onChange={handleChange} placeholder="English translation of role" />
                    </div>
                  </div>
                </div>

                {/* Common Fields */}
                <div>
                  <label className="text-sm font-medium">Nome</label>
                  <Input name="author" value={formData.author || ''} onChange={handleChange} required />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active || false}
                    onChange={handleChange}
                  />
                  <label htmlFor="active" className="text-sm font-medium">Ativo</label>
                </div>
              </>
            )}

            {/* Region Form */}
            {!editingSection && (activeTab === 'regions' || editingItem?.type === 'region') && (
              <>
                {/* Portuguese Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇵🇹</span> Português
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Nome da Região (PT)</label>
                      <Input name="name" value={formData.name || ''} onChange={handleChange} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Descrição (PT)</label>
                      <Textarea name="description" value={formData.description || ''} onChange={handleChange} rows={3} required />
                    </div>
                  </div>
                </div>

                {/* English Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇬🇧</span> English
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Region Name (EN)</label>
                      <Input name="name_en" value={formData.name_en || ''} onChange={handleChange} placeholder="English translation" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Description (EN)</label>
                      <Textarea name="description_en" value={formData.description_en || ''} onChange={handleChange} rows={3} placeholder="English translation" />
                    </div>
                  </div>
                </div>

                {/* Common Fields */}
                <div>
                  <label className="text-sm font-medium">Faixa de Preço (opcional)</label>
                  <Input name="priceRange" value={formData.priceRange || ''} onChange={handleChange} placeholder="Ex: €250.000 - €500.000" />
                </div>
                <ImageUpload
                  label="Imagem"
                  value={formData.image || ''}
                  onChange={(url) => setFormData(prev => ({ ...prev, image: url }))}
                  required
                />
                <div>
                  <label className="text-sm font-medium">Ordem de Exibição</label>
                  <Input type="number" name="order" value={formData.order || 0} onChange={handleChange} min="0" required />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="active-region"
                    name="active"
                    checked={formData.active || false}
                    onChange={handleChange}
                  />
                  <label htmlFor="active-region" className="text-sm font-medium">Ativo</label>
                </div>
              </>
            )}

            {/* Home Content Forms */}
            {editingSection === 'hero' && (
              <>
                {/* Portuguese Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇵🇹</span> Português
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Título (PT)</label>
                      <Input name="title" value={formData.title || ''} onChange={handleChange} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Subtítulo (PT)</label>
                      <Input name="subtitle" value={formData.subtitle || ''} onChange={handleChange} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Texto do Botão CTA (PT) (opcional)</label>
                      <Input 
                        name="ctaText" 
                        value={formData.ctaText || ''} 
                        onChange={handleChange}
                        placeholder="Ex: Veja nosso novo blog"
                      />
                    </div>
                  </div>
                </div>

                {/* English Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇬🇧</span> English
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Title (EN)</label>
                      <Input name="title_en" value={formData.title_en || ''} onChange={handleChange} placeholder="English translation" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Subtitle (EN)</label>
                      <Input name="subtitle_en" value={formData.subtitle_en || ''} onChange={handleChange} placeholder="English translation" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">CTA Button Text (EN) (optional)</label>
                      <Input 
                        name="ctaText_en" 
                        value={formData.ctaText_en || ''} 
                        onChange={handleChange} 
                        placeholder="Ex: Check our new blog post"
                      />
                    </div>
                  </div>
                </div>

                {/* Common Fields */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-semibold mb-3">Configurações Visuais</h3>
                  
                  <ImageUpload
                    label="Imagem de Fundo (Opcional)"
                    value={formData.image || ''}
                    onChange={(url) => setFormData({ ...formData, image: url })}
                  />
                  <p className="text-xs text-slate-500 mt-1 mb-4">
                    💡 Dica: Use para datas comemorativas (Natal, Ano Novo, etc). Se não adicionar, será usado o gradiente azul padrão.
                  </p>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-semibold mb-3">Botão de Ação (CTA) - Opcional</h3>
                  <p className="text-xs text-slate-500 mb-3">
                    💡 Use para promover posts do blog, propriedades ou qualquer link especial. Deixe vazio se não quiser botão.
                  </p>
                  
                  <div>
                    <label className="text-sm font-medium">Link do CTA</label>
                    <Input 
                      name="ctaLink" 
                      value={formData.ctaLink || ''} 
                      onChange={handleChange} 
                      placeholder="Ex: /blog/novo-post ou #contact"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Exemplos: <code className="bg-slate-100 px-1 rounded">#contact</code> (seção), 
                      <code className="bg-slate-100 px-1 rounded ml-1">/blog/1</code> (página interna), 
                      <code className="bg-slate-100 px-1 rounded ml-1">https://...</code> (link externo)
                    </p>
                  </div>
                </div>
              </>
            )}

            {editingSection === 'intro' && (
              <>
                {/* Portuguese Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇵🇹</span> Português
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Título (PT)</label>
                      <Input name="title" value={formData.title || ''} onChange={handleChange} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Descrição (PT)</label>
                      <Textarea name="description" value={formData.description || ''} onChange={handleChange} rows={4} required />
                    </div>
                  </div>
                </div>

                {/* English Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇬🇧</span> English
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Title (EN)</label>
                      <Input name="title_en" value={formData.title_en || ''} onChange={handleChange} placeholder="English translation" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Description (EN)</label>
                      <Textarea name="description_en" value={formData.description_en || ''} onChange={handleChange} rows={4} placeholder="English translation" />
                    </div>
                  </div>
                </div>
              </>
            )}

            {editingSection === 'about' && (
              <>
                {/* Portuguese Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇵🇹</span> Português
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Título (PT)</label>
                      <Input name="title" value={formData.title || ''} onChange={handleChange} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Descrição (PT)</label>
                      <Textarea name="description" value={formData.description || ''} onChange={handleChange} rows={4} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Destaque (PT)</label>
                      <Input name="highlight" value={formData.highlight || ''} onChange={handleChange} required />
                    </div>
                  </div>
                </div>

                {/* English Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇬🇧</span> English
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Title (EN)</label>
                      <Input name="title_en" value={formData.title_en || ''} onChange={handleChange} placeholder="English translation" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Description (EN)</label>
                      <Textarea name="description_en" value={formData.description_en || ''} onChange={handleChange} rows={4} placeholder="English translation" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Highlight (EN)</label>
                      <Input name="highlight_en" value={formData.highlight_en || ''} onChange={handleChange} placeholder="English translation" />
                    </div>
                  </div>
                </div>

                {/* Common Fields */}
                <ImageUpload
                  label="Imagem da Seção"
                  value={formData.image || ''}
                  onChange={(url) => setFormData({ ...formData, image: url })}
                />
              </>
            )}

            {editingSection === 'stats' && (
              <>
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇵🇹</span> Português
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Estatística 1 - Texto Principal (PT)</label>
                        <Input name="stat1_value" value={formData.stat1_value || ''} onChange={handleChange} required placeholder="Ex: 15+ Anos" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Estatística 1 - Texto Secundário (PT)</label>
                        <Input name="stat1_label" value={formData.stat1_label || ''} onChange={handleChange} required placeholder="Ex: de Experiência" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Estatística 2 - Texto Principal (PT)</label>
                        <Input name="stat2_value" value={formData.stat2_value || ''} onChange={handleChange} required placeholder="Ex: 500+" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Estatística 2 - Texto Secundário (PT)</label>
                        <Input name="stat2_label" value={formData.stat2_label || ''} onChange={handleChange} required placeholder="Ex: Clientes Satisfeitos" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Estatística 3 - Texto Principal (PT)</label>
                        <Input name="stat3_value" value={formData.stat3_value || ''} onChange={handleChange} required placeholder="Ex: 100%" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Estatística 3 - Texto Secundário (PT)</label>
                        <Input name="stat3_label" value={formData.stat3_label || ''} onChange={handleChange} required placeholder="Ex: Dedicação" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇬🇧</span> English
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Stat 1 - Texto Principal (EN)</label>
                        <Input name="stat1_value_en" value={formData.stat1_value_en || ''} onChange={handleChange} placeholder="Ex: 15+ Years" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Stat 1 - Texto Secundário (EN)</label>
                        <Input name="stat1_label_en" value={formData.stat1_label_en || ''} onChange={handleChange} placeholder="Ex: of Experience" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Stat 2 - Texto Principal (EN)</label>
                        <Input name="stat2_value_en" value={formData.stat2_value_en || ''} onChange={handleChange} placeholder="Ex: 500+" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Stat 2 - Texto Secundário (EN)</label>
                        <Input name="stat2_label_en" value={formData.stat2_label_en || ''} onChange={handleChange} placeholder="Ex: Happy Clients" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Stat 3 - Texto Principal (EN)</label>
                        <Input name="stat3_value_en" value={formData.stat3_value_en || ''} onChange={handleChange} placeholder="Ex: 100%" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Stat 3 - Texto Secundário (EN)</label>
                        <Input name="stat3_label_en" value={formData.stat3_label_en || ''} onChange={handleChange} placeholder="Ex: Dedication" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">⚙️ Formatação</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium block mb-2">Tamanho do Texto Principal</label>
                      <select 
                        name="value_size" 
                        value={formData.value_size || 'large'} 
                        onChange={handleChange}
                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="small">Pequeno</option>
                        <option value="medium">Médio</option>
                        <option value="large">Grande (padrão)</option>
                        <option value="xlarge">Extra Grande</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Use "Pequeno" ou "Médio" para textos longos ou frases</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-2">Tamanho do Texto Secundário</label>
                      <select 
                        name="label_size" 
                        value={formData.label_size || 'medium'} 
                        onChange={handleChange}
                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="small">Pequeno</option>
                        <option value="medium">Médio (padrão)</option>
                        <option value="large">Grande</option>
                        <option value="xlarge">Extra Grande</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Ajuste conforme o tamanho da frase secundária</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {editingSection === 'headerFooter' && (
              <>
                {/* Portuguese Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇵🇹</span> Português
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Descrição do Rodapé (PT)</label>
                      <Textarea name="footerDescription" value={formData.footerDescription || ''} onChange={handleChange} rows={2} required />
                    </div>
                  </div>
                </div>

                {/* English Fields */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🇬🇧</span> English
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Footer Description (EN)</label>
                      <Textarea name="footerDescription_en" value={formData.footerDescription_en || ''} onChange={handleChange} rows={2} placeholder="English translation" />
                    </div>
                  </div>
                </div>

                {/* Common Fields */}
                <div>
                  <label className="text-sm font-medium">Nome da Empresa</label>
                  <Input name="companyName" value={formData.companyName || ''} onChange={handleChange} required />
                </div>
                <div>
                  <label className="text-sm font-medium">Telefone</label>
                  <Input name="phone" value={formData.phone || ''} onChange={handleChange} required />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input name="email" type="email" value={formData.email || ''} onChange={handleChange} required />
                </div>
                <div>
                  <label className="text-sm font-medium">Endereço</label>
                  <Input name="address" value={formData.address || ''} onChange={handleChange} required />
                </div>
                <ImageUpload
                  label="Logo da Empresa"
                  value={formData.logo || ''}
                  onChange={(url) => setFormData({ ...formData, logo: url })}
                />
                
                {/* Social Media Section */}
                <div className="space-y-4 border-t pt-4">
                  <label className="text-sm font-medium">Redes Sociais</label>
                  <p className="text-xs text-slate-500">Adicione links para suas redes sociais</p>
                  {(formData.socialMedia || []).map((social, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div>
                        <label className="text-xs font-medium">Plataforma</label>
                        <select
                          className="w-full px-3 py-2 border border-slate-300 rounded-md"
                          value={social.platform || 'facebook'}
                          onChange={(e) => {
                            const newSocialMedia = [...(formData.socialMedia || [])];
                            newSocialMedia[index] = { ...newSocialMedia[index], platform: e.target.value };
                            setFormData({ ...formData, socialMedia: newSocialMedia });
                          }}
                        >
                          <option value="facebook">Facebook</option>
                          <option value="instagram">Instagram</option>
                          <option value="linkedin">LinkedIn</option>
                          <option value="twitter">Twitter/X</option>
                          <option value="youtube">YouTube</option>
                          <option value="whatsapp">WhatsApp</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">URL</label>
                        <Input 
                          value={social.url || ''} 
                          onChange={(e) => {
                            const newSocialMedia = [...(formData.socialMedia || [])];
                            newSocialMedia[index] = { ...newSocialMedia[index], url: e.target.value };
                            setFormData({ ...formData, socialMedia: newSocialMedia });
                          }}
                          placeholder="https://facebook.com/..."
                          required 
                        />
                      </div>
                      <Button 
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          const newSocialMedia = (formData.socialMedia || []).filter((_, i) => i !== index);
                          setFormData({ ...formData, socialMedia: newSocialMedia });
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remover Rede Social
                      </Button>
                    </div>
                  ))}
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const newSocialMedia = [...(formData.socialMedia || []), { id: Date.now().toString(), platform: 'facebook', url: '' }];
                      setFormData({ ...formData, socialMedia: newSocialMedia });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Rede Social
                  </Button>
                </div>
              </>
            )}


            {editingSection === 'services' && (
              <>
                <div>
                  <label className="text-sm font-medium">Título da Seção</label>
                  <Input name="title" value={formData.title || ''} onChange={handleChange} required />
                </div>
                <div>
                  <label className="text-sm font-medium">Subtítulo (opcional)</label>
                  <Input name="subtitle" value={formData.subtitle || ''} onChange={handleChange} />
                </div>
                <div className="space-y-4">
                  <label className="text-sm font-medium">Serviços</label>
                  <p className="text-xs text-slate-500">Edite os serviços abaixo:</p>
                  {(formData.services || []).map((service, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div>
                        <label className="text-xs font-medium">Título do Serviço {index + 1}</label>
                        <Input 
                          value={service.title || ''} 
                          onChange={(e) => {
                            const newServices = [...(formData.services || [])];
                            newServices[index] = { ...newServices[index], title: e.target.value };
                            setFormData({ ...formData, services: newServices });
                          }}
                          required 
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Descrição</label>
                        <Textarea 
                          value={service.description || ''} 
                          onChange={(e) => {
                            const newServices = [...(formData.services || [])];
                            newServices[index] = { ...newServices[index], description: e.target.value };
                            setFormData({ ...formData, services: newServices });
                          }}
                          rows={2}
                          required 
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Ícone</label>
                        <select
                          className="w-full px-3 py-2 border border-slate-300 rounded-md"
                          value={service.icon || 'briefcase'}
                          onChange={(e) => {
                            const newServices = [...(formData.services || [])];
                            newServices[index] = { ...newServices[index], icon: e.target.value };
                            setFormData({ ...formData, services: newServices });
                          }}
                        >
                          <option value="search">Pesquisa</option>
                          <option value="target">Alvo</option>
                          <option value="handshake">Aperto de Mão</option>
                          <option value="briefcase">Maleta</option>
                          <option value="globe">Globo</option>
                          <option value="gift">Presente</option>
                        </select>
                      </div>
                      <Button 
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          const newServices = (formData.services || []).filter((_, i) => i !== index);
                          setFormData({ ...formData, services: newServices });
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remover Serviço
                      </Button>
                    </div>
                  ))}
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const newServices = [...(formData.services || []), { id: Date.now().toString(), title: '', description: '', icon: 'briefcase' }];
                      setFormData({ ...formData, services: newServices });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Serviço
                  </Button>
                </div>
              </>
            )}

            {/* Decorative section removed */}


            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#2E5E99] hover:bg-[#0D2440] text-white">
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rich Blog Editor Modal */}
      {showRichEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen px-2 sm:px-4 py-4 sm:py-8">
            <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-xl p-3 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
                {richEditorData ? 'Editar Post' : 'Criar Novo Post'}
              </h2>
              <RichBlogEditor
                initialData={richEditorData}
                onSave={handleRichBlogSave}
                onCancel={handleRichBlogCancel}
              />
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AllContentManagement;
